import {
  AutomaticStreakConfig,
  AutomaticStreakConfigResponse,
  CampaignAutopilotConfig,
  CampaignAutopilotConfigV1,
} from "@cede/types";
import { isServerApiError, isServerApiRfcError, QueryKeys } from "@cede/utils";
import { useAutomaticStreakEpochLimit } from "./useAutomaticStreakEpochLimit";
import { useCampaign } from "@/hooks/campaign/useCampaign";
import { useHubUser } from "@/hooks/user/useHubUser";
import { getAutomaticStreakConfig } from "@/utils/api";
import { useQuery } from "@tanstack/react-query";

/**
 * Hook to get autopilot configuration.
 *
 * Priority:
 * 1. If the current epoch group has a V2 autopilotConfig, use it directly
 * 2. If the campaign has a V2 autopilotConfig, use it directly (config is in campaign data)
 * 3. If the campaign has a V1 autopilotConfig, use it directly
 * 4. Otherwise, fall back to the global config endpoint (always V1 format)
 *
 * For V2 configs with maxAutopilotsPerEpoch, also fetches the epoch limit status.
 *
 * @param campaignName - The campaign name to get config for
 */
export const useAutomaticStreakConfig = (campaignName?: string) => {
  const { auth, authenticated } = useHubUser();
  const { campaign, currentEpoch, nextEpoch, isLoading: isCampaignLoading } = useCampaign(campaignName);

  // Resolve autopilot config: epoch group > campaign > null. When the campaign
  // hasn't started yet there's no current epoch, so fall back to the first
  // upcoming epoch's group — otherwise coming-soon campaigns whose autopilot
  // config lives on the epoch group would silently appear "not autopilot
  // enabled" and the user couldn't queue up ahead of launch.
  const referenceEpoch = currentEpoch ?? nextEpoch;
  const epochGroup =
    referenceEpoch?.epochGroupId != null
      ? campaign?.epochGroups?.find((eg) => eg.id === referenceEpoch.epochGroupId)
      : undefined;
  const campaignAutopilotConfig = epochGroup?.autopilotConfig ?? campaign?.autopilotConfig;

  const isV2Config = campaignAutopilotConfig?.version === 2;
  const hasEpochLimit = isV2Config && campaignAutopilotConfig?.data.maxAutopilotsPerEpoch !== undefined;

  // Only fetch global config if campaign doesn't have its own config
  const shouldFetchGlobalConfig = authenticated && !campaignAutopilotConfig && !isCampaignLoading;

  // Fetch epoch limit status using dedicated hook (only for V2 configs with maxAutopilotsPerEpoch)
  const {
    isLimitReached: isEpochLimitReached,
    isLoading: isEpochLimitLoading,
    error: epochLimitError,
    refetch: refetchEpochLimit,
  } = useAutomaticStreakEpochLimit(campaignName, currentEpoch?.id, hasEpochLimit && !isCampaignLoading);

  const globalConfigQuery = useQuery({
    queryKey: [QueryKeys.AUTOMATIC_STREAK_CONFIG],
    queryFn: async (): Promise<AutomaticStreakConfig> => {
      const result = await getAutomaticStreakConfig(auth);

      if (isServerApiError(result) || isServerApiRfcError(result)) {
        throw new Error("Failed to fetch automatic streak config");
      }

      return (result as AutomaticStreakConfigResponse).config;
    },
    enabled: shouldFetchGlobalConfig,
    staleTime: 1 * 60 * 1000, // 1 minute - matches API cache TTL
    gcTime: 1 * 60 * 1000,
    retry: 2,
  });

  // Convert global config to V1 format if needed
  const globalConfigAsV1: CampaignAutopilotConfigV1 | undefined = globalConfigQuery.data
    ? {
        version: 1,
        data: {
          enabled: globalConfigQuery.data.enabled,
          minTrades: globalConfigQuery.data.minTrades,
          maxTrades: globalConfigQuery.data.maxTrades,
          minVolumePerTrade: globalConfigQuery.data.minVolumePerTrade,
          maxVolumePerTrade: globalConfigQuery.data.maxVolumePerTrade,
          minVolumeDifference: globalConfigQuery.data.minVolumeDifference,
          validIntervals: globalConfigQuery.data.validIntervals,
          defaultStartDelayMinutes: globalConfigQuery.data.defaultStartDelayMinutes,
          defaultCycles: globalConfigQuery.data.defaultCycles,
          volumeVariancePercent: globalConfigQuery.data.volumeVariancePercent,
          defaultVolumePerTrade: globalConfigQuery.data.defaultVolumePerTrade,
        },
      }
    : undefined;

  // Determine the final config to use
  const autopilotConfig: CampaignAutopilotConfig | undefined = campaignAutopilotConfig ?? globalConfigAsV1;

  // Determine if autopilot is enabled
  // For campaign config: always enabled if config exists
  // For global config: check the enabled flag
  const isEnabled = campaignAutopilotConfig ? true : (globalConfigQuery.data?.enabled ?? false);

  // V2-only opt-out: when true, the queue is skipped and submit creates a
  // streak immediately. V1 has no queue concept, so this stays false there.
  const isQueueDisabled = autopilotConfig?.version === 2 && autopilotConfig.data.disableQueueSystem === true;

  const isLoading =
    isCampaignLoading || (shouldFetchGlobalConfig && globalConfigQuery.isLoading) || isEpochLimitLoading;

  return {
    /** The autopilot config (V1 or V2 format) */
    autopilotConfig,
    /** Whether autopilot is enabled for this campaign */
    isEnabled,
    /** The config version (1 or 2), or undefined if no config */
    configVersion: autopilotConfig?.version,
    /** Whether the config is loading */
    isLoading,
    /** Error message if config fetch failed */
    error: globalConfigQuery.error?.message ?? epochLimitError,
    /** Refetch the config */
    refetch: hasEpochLimit ? refetchEpochLimit : globalConfigQuery.refetch,
    /** Whether the epoch limit is reached (V2 only, false if no limit configured) */
    isEpochLimitReached,
    /** Whether the picker queue is disabled (V2 only). When true, submit creates a streak immediately. */
    isQueueDisabled,
    // Legacy compatibility: expose the raw config data for V1
    /** @deprecated Use autopilotConfig instead */
    config: autopilotConfig?.version === 1 ? { ...autopilotConfig.data, enabled: isEnabled } : undefined,
  };
};
